import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { BarChart3, FileText, Plus, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppShell } from '@/components/layout/AppShell';
import { DEFAULT_INSTRUMENT_ID } from '@/lib/schema/loader';
import {
  deleteObservation,
  getObservation,
  listObservations,
  upsertObservation,
} from '@/lib/storage/db';
import type { ObservationRecord } from '@/lib/state/types';
import { emptyRecord } from '@/lib/state/record';
import { itemsInOrder } from '@/lib/schema/loader';
import { parseObservationImport } from '@/lib/state/zod';

export function Landing() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<ObservationRecord[]>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refresh = () => listObservations().then(setRecords);

  useEffect(() => {
    void refresh();
  }, []);

  const startNew = async () => {
    const rec = emptyRecord(uuid());
    await upsertObservation(rec);
    const first = itemsInOrder('FCCERS-3')[0];
    navigate(`/observation/${rec.id}/metadata`, { replace: true });
    void first; // route flow: metadata → first item
  };

  const remove = async (id: string) => {
    await deleteObservation(id);
    void refresh();
  };

  const importFromFile = async (file: File) => {
    setImportError(null);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const parsed = parseObservationImport(json);
      if (!parsed.ok) {
        setImportError(parsed.error);
        return;
      }
      let record = parsed.record;
      // Collision: id already on this device → mint a new id + timestamp
      // so imports are additive, never clobbering local drafts.
      const existing = await getObservation(record.id);
      if (existing) {
        record = {
          ...record,
          id: uuid(),
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
      }
      await upsertObservation(record);
      void refresh();
      // Navigate into the imported draft at the first item.
      const first = itemsInOrder('FCCERS-3')[0];
      if (first) {
        navigate(`/observation/${record.id}/items/${first.id}`);
      }
    } catch (err) {
      setImportError(
        err instanceof Error ? err.message : 'Could not read file as JSON.',
      );
    }
  };

  return (
    <AppShell instrumentId={DEFAULT_INSTRUMENT_ID} showSidebar={false}>
      <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col gap-8 px-6 py-12">
        <section>
          <h2 className="font-serif text-3xl font-bold text-dece-navy">
            FCCERS-3 Observation Tool
          </h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Digital companion to the Family Child Care Environment Rating Scale,
            Third Edition. Score 462 indicators across 6 subscales, see 1–7
            ratings compute live, and export your observation as JSON.
            Printable PDF reports coming soon.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button size="lg" onClick={startNew} className="gap-2">
              <Plus className="h-4 w-4" />
              Start new observation
            </Button>
            <Button asChild size="lg" variant="outline" className="gap-2">
              <Link to="/dashboard">
                <BarChart3 className="h-4 w-4" />
                Dashboard
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="gap-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4" />
              Import from JSON
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset so selecting the same file twice re-triggers onChange.
                e.target.value = '';
                if (file) void importFromFile(file);
              }}
            />
          </div>
          {importError ? (
            <div
              role="alert"
              className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {importError}
            </div>
          ) : null}
        </section>
        <section>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Drafts on this device
          </h3>
          {records.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">
              No saved observations yet. Start a new one above.
            </p>
          ) : (
            <ul className="mt-3 divide-y rounded-md border bg-card">
              {records.map((r) => {
                const firstItemId = itemsInOrder('FCCERS-3')[0]?.id;
                const progress = Object.values(r.itemStatus).filter(
                  (s) => s === 'complete',
                ).length;
                return (
                  <li
                    key={r.id}
                    className="flex items-center justify-between gap-4 px-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <FileText className="h-4 w-4 text-dece-sky" />
                        {r.metadata.siteName || 'Untitled observation'}
                        <span className="text-xs text-muted-foreground">
                          · {r.status}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Updated {new Date(r.updatedAt).toLocaleString()} · {progress}/33 items complete
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button asChild variant="outline" size="sm">
                        <Link
                          to={
                            firstItemId
                              ? `/observation/${r.id}/items/${firstItemId}`
                              : '/'
                          }
                        >
                          Resume
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete draft"
                        onClick={() => remove(r.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}
